// src/contexts/LanguageContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

// Create language context
const LanguageContext = createContext();

// Default language set to browser language if available and supported, otherwise English
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  // Check if browser language starts with 'de', otherwise default to English
  return browserLang && browserLang.startsWith('de') ? 'de' : 'en';
};

// Language provider component
export const LanguageProvider = ({ children }) => {
  // Initialize state with either saved preference or browser language
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('lucent-language');
    return savedLanguage || getBrowserLanguage();
  });

  // Save language preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('lucent-language', language);
    
    // Update html lang attribute
    document.documentElement.lang = language;
    
    // Update canonical and hreflang links
    updateHreflangTags(language);
  }, [language]);

  // Function to update hreflang tags
  const updateHreflangTags = (currentLang) => {
    // Get the current path without language prefix if it exists
    const path = window.location.pathname;
    const basePath = path.replace(/^\/(en|de)\//, '/');
    const baseUrl = window.location.origin;
    
    // Remove existing hreflang and canonical links
    const existingLinks = document.querySelectorAll('link[rel="alternate"], link[rel="canonical"]');
    existingLinks.forEach(link => link.remove());
    
    // Add new canonical link
    const canonicalUrl = `${baseUrl}/${currentLang}${basePath}`;
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);
    
    // Add hreflang links
    const languages = ['en', 'de'];
    languages.forEach(lang => {
      const hreflangLink = document.createElement('link');
      hreflangLink.rel = 'alternate';
      hreflangLink.hreflang = lang;
      hreflangLink.href = `${baseUrl}/${lang}${basePath}`;
      document.head.appendChild(hreflangLink);
    });
    
    // Add x-default hreflang
    const defaultHreflangLink = document.createElement('link');
    defaultHreflangLink.rel = 'alternate';
    defaultHreflangLink.hreflang = 'x-default';
    defaultHreflangLink.href = `${baseUrl}/en${basePath}`;
    document.head.appendChild(defaultHreflangLink);
  };

  // Change language function
  const changeLanguage = (newLanguage) => {
    if (newLanguage === 'en' || newLanguage === 'de') {
      setLanguage(newLanguage);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;